import { useState, useEffect, useRef } from "react";

const productsMenu = [
  {
    category: "Women's Health",
    items: ["BeautyPro", "FeriPro Plus", "FeriPro", "OmegaPro Soft Gel Capsules", "OsteoPro"],
  },
  {
    category: "Men's Health",
    items: ["FeriPro Plus", "FeriPro", "OmegaPro Soft Gel Capsules"],
  },
  {
    category: "Children & Babies",
    items: ["Gripe Water", "MultiPro", "OmegaPro Syrup"],
  },
  {
    category: "Beauty",
    items: ["BeautyPro"],
  },
  {
    category: "Pregnancy",
    items: ["FeriPro Plus", "FeriPro", "OsteoPro", "PregnaPro"],
  },
  {
    category: "Bones & Joints Care",
    items: ["OmegaPro Soft Gel Capsules", "OsteoPro"],
  },
  {
    category: "Heart Health",
    items: ["OmegaPro Soft Gel Capsules"],
  },
  {
    category: "Brain & Memory",
    items: ["OmegaPro Soft Gel Capsules", "OmegaPro Syrup"],
  },
  {
    category: "Energy",
    items: ["FeriPro Plus", "FeriPro", "MultiPro"],
  },
  {
    category: "Menopause",
    items: ["OsteoPro"],
  },
  {
    category: "Electrolytes",
    items: ["Electrolyte Syrup"],
  },
];

const nutritionalMenu = [
  {
    category: "Minerals",
    items: ["Calcium", "Chromium", "Copper", "Iodine", "Iron", "Magnesium", "Manganese", "Molybdenum", "Selenium", "Zinc"],
  },
  {
    category: "Vitamins",
    items: [
      "Beta Carotene",
      "Biotin (Vitamin B7)",
      "Cobalamin (Vitamin B12)",
      "Folic Acid",
      "Niacin (Vitamin B3)",
      "Pantothenic Acid (Vitamin B5)",
      "Pyridoxine (Vitamin B6)",
      "Riboflavin (Vitamin B2)",
      "Thiamin (Vitamin B1)",
      "Vitamin C",
      "Vitamin D",
      "Vitamin E",
      "Vitamin K",
    ],
  },
];

const healthToolsMenu = ["BMI", "Pregnancy due date"];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const navRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleMouseEnter = (menu: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveMenu(menu);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveMenu(null), 150);
  };

  return (
    <header className={`w-full z-50 transition-all duration-300 ${scrolled ? "fixed top-0 shadow-md" : "relative"}`} ref={navRef}>
      {/* Top bar */}
      <div className={`w-full bg-white transition-all duration-300 ${scrolled ? "hidden" : "block"}`}>
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between h-24">
          <a href="/" className="flex-shrink-0">
            <img
              src="https://nutravitltd.co.uk/images/logo.png"
              alt="Vitatec Nutrition"
              className="h-20 object-contain"
            />
          </a>
          <div className="flex items-center gap-3">
            {searchOpen ? (
              <div className="flex items-center border border-gray-300 rounded-full overflow-hidden px-3 py-1.5 bg-white">
                <i className="ri-search-line text-gray-500 text-sm mr-2"></i>
                <input
                  type="text"
                  className="text-sm outline-none w-48"
                  placeholder="Search..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  autoFocus
                />
                <button onClick={() => setSearchOpen(false)} className="ml-2 cursor-pointer">
                  <i className="ri-close-line text-gray-500 text-sm"></i>
                </button>
              </div>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-search-line text-gray-600 text-lg"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Nav bar */}
      <nav
        className={`w-full transition-all duration-300 ${
          scrolled ? "bg-white border-b border-gray-200" : "bg-[#2c4a5a]"
        }`}
      >
        <div className="max-w-6xl mx-auto px-6 flex items-center h-12">
          {scrolled && (
            <a href="/" className="mr-6 flex-shrink-0">
              <img
                src="https://nutravitltd.co.uk/images/logo.png"
                alt="Vitatec Nutrition"
                className="h-9 object-contain"
              />
            </a>
          )}
          <ul className="flex items-center gap-1 flex-1">
            {/* Home */}
            <li>
              <a
                href="/"
                className={`whitespace-nowrap text-sm font-medium px-4 py-3 block transition-colors ${
                  scrolled ? "text-gray-700 hover:text-teal-600" : "text-white hover:text-teal-300"
                }`}
              >
                Home
              </a>
            </li>

            {/* Our Products */}
            <li
              className="relative"
              onMouseEnter={() => handleMouseEnter("products")}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`whitespace-nowrap text-sm font-medium px-4 py-3 flex items-center gap-1 transition-colors cursor-pointer ${
                  scrolled ? "text-gray-700 hover:text-teal-600" : "text-white hover:text-teal-300"
                }`}
              >
                Our Products
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${activeMenu === "products" ? "rotate-180" : ""}`}></i>
              </button>
              {activeMenu === "products" && (
                <div
                  className="absolute top-full left-0 bg-white shadow-xl border border-gray-100 rounded-lg p-6 z-50 min-w-max"
                  style={{ width: "780px" }}
                  onMouseEnter={() => handleMouseEnter("products")}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="grid grid-cols-4 gap-5">
                    {productsMenu.map((group) => (
                      <div key={group.category}>
                        <h6 className="text-xs font-bold uppercase text-teal-700 tracking-wider mb-2 border-b border-teal-100 pb-1">
                          {group.category}
                        </h6>
                        <ul className="space-y-1">
                          {group.items.map((item) => (
                            <li key={item}>
                              <a
                                href="#"
                                className="text-xs text-gray-600 hover:text-teal-600 transition-colors block leading-snug"
                              >
                                {item}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </li>

            {/* Nutritional Guide */}
            <li
              className="relative"
              onMouseEnter={() => handleMouseEnter("nutritional")}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`whitespace-nowrap text-sm font-medium px-4 py-3 flex items-center gap-1 transition-colors cursor-pointer ${
                  scrolled ? "text-gray-700 hover:text-teal-600" : "text-white hover:text-teal-300"
                }`}
              >
                Nutritional Guide
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${activeMenu === "nutritional" ? "rotate-180" : ""}`}></i>
              </button>
              {activeMenu === "nutritional" && (
                <div
                  className="absolute top-full left-0 bg-white shadow-xl border border-gray-100 rounded-lg p-6 z-50"
                  style={{ width: "480px" }}
                  onMouseEnter={() => handleMouseEnter("nutritional")}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="grid grid-cols-2 gap-5">
                    {nutritionalMenu.map((group) => (
                      <div key={group.category}>
                        <h6 className="text-xs font-bold uppercase text-teal-700 tracking-wider mb-2 border-b border-teal-100 pb-1">
                          {group.category}
                        </h6>
                        <ul className="space-y-1">
                          {group.items.map((item) => (
                            <li key={item}>
                              <a
                                href="#"
                                className="text-xs text-gray-600 hover:text-teal-600 transition-colors block"
                              >
                                {item}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </li>

            {/* Health Tools */}
            <li
              className="relative"
              onMouseEnter={() => handleMouseEnter("health")}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`whitespace-nowrap text-sm font-medium px-4 py-3 flex items-center gap-1 transition-colors cursor-pointer ${
                  scrolled ? "text-gray-700 hover:text-teal-600" : "text-white hover:text-teal-300"
                }`}
              >
                Health Tools
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${activeMenu === "health" ? "rotate-180" : ""}`}></i>
              </button>
              {activeMenu === "health" && (
                <div
                  className="absolute top-full left-0 bg-white shadow-xl border border-gray-100 rounded-lg py-2 z-50 min-w-40"
                  onMouseEnter={() => handleMouseEnter("health")}
                  onMouseLeave={handleMouseLeave}
                >
                  {healthToolsMenu.map((item) => (
                    <a
                      key={item}
                      href="#"
                      className="block px-4 py-2 text-sm text-gray-600 hover:bg-teal-50 hover:text-teal-700 transition-colors"
                    >
                      {item}
                    </a>
                  ))}
                </div>
              )}
            </li>

            {/* About Us */}
            <li>
              <a
                href="#"
                className={`whitespace-nowrap text-sm font-medium px-4 py-3 block transition-colors ${
                  scrolled ? "text-gray-700 hover:text-teal-600" : "text-white hover:text-teal-300"
                }`}
              >
                About Us
              </a>
            </li>

            {/* Contact us */}
            <li>
              <a
                href="#"
                className={`whitespace-nowrap text-sm font-medium px-4 py-3 block transition-colors ${
                  scrolled ? "text-gray-700 hover:text-teal-600" : "text-white hover:text-teal-300"
                }`}
              >
                Contact us
              </a>
            </li>

            {/* News */}
            <li>
              <a
                href="#"
                className={`whitespace-nowrap text-sm font-medium px-4 py-3 block transition-colors ${
                  scrolled ? "text-gray-700 hover:text-teal-600" : "text-white hover:text-teal-300"
                }`}
              >
                News
              </a>
            </li>
          </ul>
          {scrolled && (
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer ml-2"
            >
              <i className="ri-search-line text-gray-600"></i>
            </button>
          )}
        </div>
        {scrolled && searchOpen && (
          <div className="border-t border-gray-100 bg-white px-6 py-2 flex items-center">
            <div className="max-w-6xl mx-auto w-full flex items-center gap-2">
              <i className="ri-search-line text-gray-400 text-sm"></i>
              <input
                type="text"
                className="text-sm outline-none flex-1"
                placeholder="Search products, guides..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                autoFocus
              />
              <button onClick={() => setSearchOpen(false)} className="cursor-pointer">
                <i className="ri-close-line text-gray-400"></i>
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
